import { Link } from 'react-router-dom';
import Seo from '@/components/base/Seo';

const values = [
  {
    icon: 'ri-focus-3-line',
    title: '聚焦原版',
    desc: '我们的全部精力只放在一件事上：把原版漫画完整、清晰地呈现给每一位读者。',
  },
  {
    icon: 'ri-plant-line',
    title: '尊重创作',
    desc: '每一部作品背后都有作者的心血，我们尊重并推广正版创作，让好内容被更多人看见。',
  },
  {
    icon: 'ri-heart-3-line',
    title: '读者至上',
    desc: '阅读体验始终是我们的第一优先级，稳定、流畅、无打扰，是我们不变的原则。',
  },
  {
    icon: 'ri-rocket-2-line',
    title: '持续进化',
    desc: '每天都有新作品上线，我们持续优化分类、更新与推荐，陪你一起成长。',
  },
];

const principles = [
  '收录完整分镜与对白，不做无意义删减',
  '高清画质，清晰呈现每一格细节',
  '章节实时同步，最新内容第一时间上线',
  '阅读进度云端保存，多设备无缝续读',
  '严格校对更新信息，减少错漏',
  '尊重版权，鼓励并支持正版创作',
];

const testimonials = [
  {
    name: '青禾',
    text: '在这里追了几部长篇，更新很稳定，画质清晰，评论区氛围也好，是我现在唯一用的漫画平台。',
  },
  {
    name: '阿远',
    text: '最打动我的是分类做得很细，想看的题材总能很快找到，追更提醒也很及时。',
  },
  {
    name: '栗子',
    text: '从一话不落的完整剧情到高清画质，体验非常舒服，会一直支持下去。',
  },
];

export default function AboutPage() {
  return (
    <>
      <Seo
        title="关于我们 - 麻瓜漫画网原版漫画在线阅读平台"
        description="了解麻瓜漫画网：专注原版漫画在线阅读的品牌介绍、网站定位、发展理念与内容原则。我们坚持收录高清完整版、未删减原版与导演剪辑版漫画，尊重创作、读者至上。"
        keywords="麻瓜漫画网,关于我们,原版漫画,在线阅读平台,品牌介绍"
        canonical="https://uncutm.github.io/about"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'AboutPage',
          name: '关于麻瓜漫画网',
          url: 'https://uncutm.github.io/about',
          inLanguage: 'zh-CN',
          mainEntity: {
            '@type': 'Organization',
            name: '麻瓜漫画网',
            url: 'https://uncutm.github.io/',
          },
        }}
      />

      <div className="mx-auto max-w-7xl px-4 md:px-6 pt-28 md:pt-36 pb-16">
        {/* breadcrumb */}
        <nav aria-label="面包屑" className="flex items-center gap-1.5 text-sm text-foreground-500">
          <Link to="/" className="hover:text-primary-500">首页</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-950">关于我们</span>
        </nav>

        {/* intro */}
        <div className="relative mt-8 overflow-hidden rounded-2xl">
          <img
            src="https://readdy.ai/api/search-image?query=illustrated%20scene%20of%20a%20bookshelf%20filled%20with%20manga%20and%20comic%20books%20with%20warm%20glowing%20reading%20lamp%20and%20cozy%20armchair%2C%20warm%20orange%20and%20amber%20tones%2C%20anime%20style%20illustration%2C%20inviting%20atmosphere%2C%20high%20detail&width=1600&height=560&seq=about-hero&orientation=landscape"
            alt="关于麻瓜漫画网"
            className="h-[260px] md:h-[340px] w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/50 to-black/20"></div>
          <div className="absolute inset-0 halftone opacity-20"></div>
          <div className="relative flex h-full flex-col justify-center px-8 md:px-14">
            <span className="inline-flex items-center gap-2 rounded-full bg-primary-500 px-3 py-1 text-xs font-bold text-background-50 w-fit whitespace-nowrap">
              <i className="ri-magic-line"></i> ABOUT US
            </span>
            <h1 className="mt-4 font-heading text-3xl md:text-4xl text-foreground-950">关于麻瓜漫画网</h1>
            <p className="mt-3 max-w-2xl text-sm leading-7 text-foreground-800">
              一个专注原版漫画在线阅读的平台，坚持完整、高清、及时，把每一部好漫画原原本本地带给每一位读者。
            </p>
          </div>
        </div>

        {/* positioning */}
        <section className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-500/15 px-3 py-1 text-xs font-semibold text-accent-600">
              <i className="ri-compass-3-line"></i> 网站定位
            </span>
            <h2 className="mt-3 font-heading text-2xl text-foreground-950">让每一部好漫画都完整可读</h2>
            <p className="mt-4 text-sm leading-7 text-foreground-600">
              麻瓜漫画网成立于近年，目标是打造一个可靠、纯净、内容完整的原版漫画阅读空间。
              我们专注于收录高清完整版、未删减原版与导演剪辑版资源，覆盖奇幻、都市、科幻、武侠、
              悬疑、恋爱、治愈、运动、美食、惊悚等十余种题材。
            </p>
            <p className="mt-3 text-sm leading-7 text-foreground-600">
              从资深漫迷到刚入坑的新人，我们都希望你能在这里舒适地阅读、顺畅地追更，
              不错过任何一个精彩瞬间。
            </p>
          </div>
          <div className="relative">
            <div className="overflow-hidden rounded-2xl ring-1 ring-background-300/50">
              <img
                src="https://readdy.ai/api/search-image?query=person%20reading%20a%20comic%20book%20on%20a%20couch%20at%20night%20with%20warm%20lamp%20light%20and%20colorful%20manga%20covers%20scattered%20around%2C%20cozy%20warm%20orange%20tones%2C%20anime%20style%20illustration%2C%20relaxed%20enjoyable%20atmosphere%2C%20high%20detail&width=900&height=640&seq=about-read&orientation=landscape"
                alt="在麻瓜漫画网愉快阅读"
                loading="lazy"
                className="h-full w-full object-cover"
              />
            </div>
          </div>
        </section>

        {/* values */}
        <section className="mt-14">
          <div className="text-center">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-500/15 px-3 py-1 text-xs font-semibold text-primary-500">
              <i className="ri-heart-line"></i> 发展理念
            </span>
            <h2 className="mt-3 font-heading text-2xl text-foreground-950">我们的坚持</h2>
          </div>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {values.map((v) => (
              <div key={v.title} className="rounded-xl bg-background-100 p-6 ring-1 ring-background-200/50">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary-500/15 text-primary-500">
                  <i className={`${v.icon} text-2xl`}></i>
                </span>
                <h3 className="mt-4 text-sm font-bold text-foreground-950">{v.title}</h3>
                <p className="mt-2 text-xs leading-6 text-foreground-500">{v.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* principles */}
        <section className="mt-14 rounded-2xl bg-background-100 p-8 md:p-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-500/15 px-3 py-1 text-xs font-semibold text-accent-600">
                <i className="ri-checkbox-multiple-line"></i> 内容原则
              </span>
              <h2 className="mt-3 font-heading text-2xl text-foreground-950">我们如何做内容</h2>
              <p className="mt-3 text-sm leading-7 text-foreground-600">
                内容是麻瓜漫画网的生命线。我们以严格的标准把控收录、更新与校对环节，
                力求让你读到的每一话都完整、清晰、及时。
              </p>
            </div>
            <ul className="flex flex-col gap-3">
              {principles.map((p) => (
                <li key={p} className="flex items-center gap-3 text-sm text-foreground-700">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-500 text-background-50">
                    <i className="ri-check-line text-xs"></i>
                  </span>
                  {p}
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* testimonials */}
        <section className="mt-14">
          <div className="text-center">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-500/15 px-3 py-1 text-xs font-semibold text-accent-600">
              <i className="ri-double-quotes-l"></i> 读者口碑
            </span>
            <h2 className="mt-3 font-heading text-2xl text-foreground-950">来自漫迷的声音</h2>
          </div>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
            {testimonials.map((t) => (
              <figure key={t.name} className="rounded-xl bg-background-100 p-6 ring-1 ring-background-200/50">
                <div className="flex gap-0.5 text-accent-500">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <i key={i} className="ri-star-fill text-sm"></i>
                  ))}
                </div>
                <blockquote className="mt-4 text-sm leading-6 text-foreground-700">“{t.text}”</blockquote>
                <figcaption className="mt-5 flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-500/15 text-primary-500">
                    <i className="ri-user-smile-line text-lg"></i>
                  </span>
                  <span className="text-sm font-semibold text-foreground-950">{t.name}</span>
                </figcaption>
              </figure>
            ))}
          </div>
        </section>

        {/* contact CTA */}
        <section className="mt-14 flex flex-col sm:flex-row items-center justify-between gap-5 rounded-2xl bg-gradient-to-r from-primary-600 to-primary-500 p-8 md:p-10">
          <div>
            <h2 className="font-heading text-xl md:text-2xl text-background-50">有建议或想合作？欢迎联系我们</h2>
            <p className="mt-2 text-sm text-background-950/70">
              无论是内容反馈、版权合作还是意见吐槽，我们都认真倾听。
            </p>
          </div>
          <Link
            to="/contact"
            className="h-11 shrink-0 flex items-center gap-2 rounded-full bg-background-50 px-6 text-sm font-semibold text-primary-700 hover:bg-background-100 whitespace-nowrap"
          >
            <i className="ri-mail-open-line"></i> 联系我们
          </Link>
        </section>
      </div>
    </>
  );
}